import { cord, cord3, formatInput, gql2 } from "@/gql/lib";
import { Subject, InvEntityType } from "../data/model";
import { fixTier3XY, correctTier12XY } from "@/gql/cords";
import { ActionEnum, AxisType, TimeAxisRangeInput } from "@/gql/types";
import { POPULATION_TYPE, PopulationTypeHelper } from "@/common/models";
import { SharedState } from "@/common/state";
import { UseModal } from "@/components/root/modal";
import EntitiesTableModalVue from "@/components/modals/entities-table-modal/EntitiesTableModal.vue";
import { EntitiesModalProps } from "@/components/modals/entities-table-modal/model";
import { TimeFrameOption } from "@/common/state/models";
import { get_entities_modal_target_axis } from "./common";

const summary_sentence = (subject: Subject) => {
  return `Employees that have access to files in folder ${subject.name}`;
};

const get_summary = gql2<
  { subject: Subject; relative_time_frame: TimeFrameOption },
  { count: number; relative_count: number; type: InvEntityType; desc: string }
>("inv-counters.employees", ({ subject, relative_time_frame }, { frame }) => {
  const cords = (sender: boolean, time_frame: TimeAxisRangeInput) =>
    correctTier12XY({
      coordinate: {
        x: cord(
          sender ? AxisType.EmployeeSenderType : AxisType.EmployeeReceiverType
        ),
        y: cord(PopulationTypeHelper.asAxisType2(subject.type), [subject.id]),
        z: time_frame,
      },
      action: ActionEnum.UniqCount,
    });

  return {
    query: `{
     senders: tier1count(params:${formatInput(cords(true, frame))}){
        count
        uniqs
     }

     senders_relative: tier1count(params:${formatInput(
       cords(true, {
         type: relative_time_frame.type,
         values: [relative_time_frame.from],
       })
     )}){
      count
      uniqs
   }

     receivers: tier1count(params:${formatInput(cords(false, frame))}){
      count
      uniqs
     }
     receivers_relative: tier1count(params:${formatInput(
       cords(false, {
         type: relative_time_frame.type,
         values: [relative_time_frame.from],
       })
     )}){
      count
      uniqs
     }
    }`,
    mapper: (res) => {
      const { senders, senders_relative, receivers, receivers_relative } = res;
      const senders_count = senders.length > 0 ? senders[0].uniqs : 0;
      const relative_senders_count =
        senders_relative.length > 0 ? senders_relative[0].uniqs : 0;

      const receivers_count = receivers.length > 0 ? receivers[0].uniqs : 0;
      const relative_receivers_count =
        receivers_relative.length > 0 ? receivers_relative[0].uniqs : 0;
      return {
        type: POPULATION_TYPE.EMPLOYEE,
        count: senders_count + receivers_count,
        relative_count: relative_senders_count + relative_receivers_count,
        desc: summary_sentence(subject),
      };
    },
  };
});

const get_titles = (subject: Subject) => {
  return {
    main: `<b>Employees</b> that have access to files in folder <b>${subject.name}</b>`,
    events: (name: string) =>
      `Events were <b>${name}</b> sent information to <b>${subject.name}</b>`,
    files: (name: string) =>
      `File that were sent by ${name} to <b>${subject.name}</b>`,
  };
};

const _get_main_cords = (subject: Subject) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  return correctTier12XY({
    coordinate: {
      x: cord(AxisType.Employee),
      y: cord(PopulationTypeHelper.asAxisType2(subject.type), [subject.id]),
      z: {
        type,
        values: [from],
      },
    },
  }).coordinate;
};

const _get_events_cords = (subject: Subject, sub_subject_id: string) => {
  const { from, type } = SharedState.selectedTimeFrameOption.value;
  return fixTier3XY({
    x: cord3(AxisType.Employee, [sub_subject_id]),
    y: cord3(PopulationTypeHelper.asAxisType2(subject.type), [subject.id]),
    z: {
      type,
      values: [from],
    },
  });
};

const show_modal = async (subject: Subject) => {
  const titles = get_titles(subject);
  const cord = _get_main_cords(subject);
  const props: EntitiesModalProps = {
    title: titles.main,
    type: POPULATION_TYPE.EMPLOYEE,
    info_types: [],
    target_axis: get_entities_modal_target_axis(subject.id, cord),
    coordinate: cord,
    files_title: (entity) => titles.files(entity.name),
    events: {
      coordinate: (entity) => {
        return _get_events_cords(subject, entity.id);
      },
      title: (entity) => titles.events(entity.name),
    },
  };
  UseModal().show(EntitiesTableModalVue, props);
};

export default { get_summary, show_modal };
